import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { round } from "lodash";
import React from "react";
import { useNavigate } from "react-router-dom";

import ReportAPI from "../api/Report.api";
import {
  Commodity,
  Lot,
  NitrogenUsage,
  Ranch,
  ReportType,
  Shipper,
} from "../types";
import CommodityMultiSelectSearch from "./CommodityMultiSelectSearch";
import LotMultiSelectSearch from "./LotMultiSelectSearch";
import RanchSearch from "./RanchSearch";
import ShipperSearch from "./ShipperSearch";

type ReportDateType =
  | "projected_wet_date"
  | "est_harvest_date"
  | "actual_wet_date";

export default function ReportNitrogenUseForm() {
  const [page, setPage] = React.useState<number>(1);
  const [startDate, setStartDate] = React.useState<Dayjs | null>(null);
  const [endDate, setEndDate] = React.useState<Dayjs | null>(null);

  const [commodities, setCommodities] = React.useState<Commodity[]>([]);
  const [ranch, setRanch] = React.useState<Ranch | undefined>();
  const [lots, setLots] = React.useState<Lot[]>([]);

  const { data } = ReportAPI.useNitrogenUsed({
    order: undefined,
    orderBy: undefined,
    page,
    startDate: startDate?.format("YYYY-MM-DD"),
    endDate: endDate?.format("YYYY-MM-DD"),
    ranchId: ranch?.id,
    commodityIds: commodities.map((commodity) => commodity.id),
    lotIds: lots.map((lot) => lot.id),
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  // console.log(
  //   "useNitrogenUsed.startDate",
  //   startDate,
  //   startDate?.format("YYYY-MM-DD"),
  // );

  console.log("useNitrogenUsed.data.meta", data?.meta);

  return (
    <Box
      sx={{
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
        gap: "1rem",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
        <Box sx={{ flex: 1 }}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(value) => setStartDate(value)}
            sx={{ width: "100%" }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(value) => setEndDate(value)}
            sx={{ width: "100%" }}
          />
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
        <Box sx={{ flex: 1 }}>
          <RanchSearch ranch={ranch} setRanch={setRanch} />
        </Box>
        <Box sx={{ flex: 1 }}>
          {ranch && (
            <LotMultiSelectSearch ranch={ranch} lots={lots} setLots={setLots} />
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", gap: "1rem" }}>
        <Box sx={{ flex: 1 }}>
          <CommodityMultiSelectSearch
            commodities={commodities}
            setCommodities={setCommodities}
          />
        </Box>

        {/*// TODO LOTS */}
      </Box>
      {/*      <Button onClick={handleSubmit} variant="contained">
        Run Report
      </Button>*/}

      {data && data.meta && (
        <Box>
          <Typography variant="h5">
            Total Nitrogen Used:{" "}
            {round(data.meta.totalNitrogenUsed, 2).toLocaleString()}
          </Typography>
        </Box>
      )}

      <Box>
        <TableContainer>
          <Table stickyHeader aria-labelledby={"Nitrogen Report"}>
            <TableHead>
              <TableRow>
                {/*<TableCell key={"harvestId"}>Harvest ID</TableCell>*/}
                <TableCell key={"harvestEstHarvestDate"}>
                  Est. Harvest Date
                </TableCell>
                <TableCell key={"ranchName"}>Ranch Name</TableCell>
                <TableCell key={"lotableNum"}>Lot/Sublot</TableCell>
                <TableCell key={"commodityName"}>Commodity Name</TableCell>
                <TableCell key={"nitrogenUsed"}>Nitrogen Usage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data &&
                data.data &&
                data.data.map((harvestNitrogenUsage: NitrogenUsage) => (
                  <TableRow key={harvestNitrogenUsage.harvestId}>
                    {/*<TableCell key={"harvestId"}>
                      <Typography>{harvestNitrogenUsage.harvestId}</Typography>
                    </TableCell>*/}
                    <TableCell key={"harvestEstHarvestDate"}>
                      <Typography>
                        {dayjs(
                          harvestNitrogenUsage.harvestEstHarvestDate,
                        )?.format("MM/DD/YYYY")}
                      </Typography>
                    </TableCell>
                    <TableCell key={"ranchName"}>
                      <Typography>
                        {harvestNitrogenUsage.harvestLotableType === "Lot"
                          ? harvestNitrogenUsage.lotRanchName
                          : harvestNitrogenUsage.sublotRanchName}
                      </Typography>
                    </TableCell>
                    <TableCell key={"lotableNum"}>
                      <Typography>
                        {harvestNitrogenUsage.harvestLotableType === "Lot"
                          ? harvestNitrogenUsage.lotNum
                          : harvestNitrogenUsage.sublotNum}
                      </Typography>
                    </TableCell>

                    <TableCell key={"commodityName"}>
                      <Typography>
                        {harvestNitrogenUsage.commodityName}
                      </Typography>
                    </TableCell>
                    <TableCell key={"nitrogenUsed"}>
                      <Typography variant="body1">
                        {round(
                          harvestNitrogenUsage.nitrogenUsed,
                          2,
                        ).toLocaleString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {data && data.meta && (
          <TablePagination
            rowsPerPageOptions={[data.meta.perPage]}
            component="div"
            count={data.meta.count}
            rowsPerPage={data.meta.perPage}
            page={page - 1}
            onPageChange={handleChangePage}
          />
        )}
      </Box>
    </Box>
  );
}
